import { 
    Users, 
    GraduationCap, 
    AlertCircle, 
    CheckCircle,
    BookOpen,
    Activity,
    UserPlus
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAdminStore } from '../../stores/adminStore';
import { useAuthStore } from '../../stores/authStore';
import { useEffect } from 'react';
import { DueTrainingModal } from '../../components/DueTrainingModal';

const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hour12: true
    });
};

export function AdminDashboard() {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const { 
        stats, 
        recentActivity, 
        pendingInvites,
        isDueTrainingModalOpen,
        isLoading, 
        error,
        fetchAdminData,
        fetchCriticalDashboardData,
        openDueTrainingModal,
        closeDueTrainingModal
    } = useAdminStore();

    useEffect(() => {
        // Only fetch if data is not already available
        if (!stats) {
            // First load critical data quickly
            fetchCriticalDashboardData();
            
            // Then load remaining data in background
            setTimeout(() => {
                if (!recentActivity || !pendingInvites) {
                    fetchAdminData();
                }
            }, 100);
        }
    }, [stats, recentActivity, pendingInvites, fetchCriticalDashboardData, fetchAdminData]);

    const manyPendingInvites = pendingInvites && pendingInvites.length > 5;

    const handleDueTrainingClick = () => {
        openDueTrainingModal();
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="animate-pulse space-y-4">
                    <div className="h-8 w-1/4 bg-slate-200 rounded"></div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="h-32 bg-slate-200 rounded"></div>
                        <div className="h-32 bg-slate-200 rounded"></div>
                        <div className="h-32 bg-slate-200 rounded"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            <h2 className="text-2xl font-semibold text-foreground mb-6">Welcome back, {user?.name || 'Admin'}!</h2>
            
            {/* If many pending invites, show Overview and Quick Actions in first row, then Pending Invites full width */}
            {manyPendingInvites ? (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        {/* Stats Overview */}
                        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                            <h3 className="text-lg font-semibold text-foreground mb-4">Overview</h3>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <Users className="w-5 h-5 text-primary-500 mr-2" />
                                        <span className="text-slate-600">Total Employees</span>
                                    </div>
                                    <span className="font-semibold text-foreground">{stats?.totalEmployees || 0}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <GraduationCap className="w-5 h-5 text-primary-500 mr-2" />
                                        <span className="text-slate-600">Active Courses (In Progress)</span>
                                    </div>
                                    <span className="font-semibold text-foreground">{stats?.activeCourses || 0}</span>
                                </div>
                                <div 
                                    className="flex items-center justify-between cursor-pointer hover:bg-slate-50 rounded-lg transition-colors py-1 px-1 -mx-1"
                                    onClick={handleDueTrainingClick}
                                >
                                    <div className="flex items-center">
                                        <AlertCircle className="w-5 h-5 text-amber-500 mr-2" />
                                        <span className="text-slate-600">Training Due (Next 7 Days)</span>
                                    </div>
                                    <span className="font-semibold text-amber-600">{stats?.dueTrainings || 0}</span>
                                </div>
                            </div>
                        </div>
                        {/* Quick Actions */}
                        <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                            <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                            <div className="space-y-2">
                                <button 
                                    onClick={() => navigate('/admin/employees')}
                                    className="flex items-center w-full px-4 py-2 text-left text-primary-600 hover:bg-primary-50 rounded-lg transition duration-150"
                                >
                                    <Users size={16} className="mr-2"/> Manage Users
                                </button>
                                <button 
                                    onClick={() => navigate('/admin/courses')}
                                    className="flex items-center w-full px-4 py-2 text-left text-primary-600 hover:bg-primary-50 rounded-lg transition duration-150"
                                >
                                    <BookOpen size={16} className="mr-2"/> Course Management
                                </button>
                                <button 
                                    onClick={() => navigate('/admin/certificates')}
                                    className="flex items-center w-full px-4 py-2 text-left text-primary-600 hover:bg-primary-50 rounded-lg transition duration-150"
                                >
                                    <CheckCircle size={16} className="mr-2"/> View Certificates
                                </button>
                            </div>
                        </div>
                    </div>
                    {/* Pending Invitations Full Width Row */}
                    <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200 mb-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Pending Invitations</h3>
                        <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                            {pendingInvites.map((invite: any) => (
                                <div key={invite.id} className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
                                    <div className="flex-shrink-0">
                                        <UserPlus className="w-5 h-5 text-primary-500" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-foreground">{invite.name}</p>
                                        <p className="text-sm text-slate-500">
                                            {invite.invited_email}
                                            <br />
                                            Invited on {new Date(invite.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    {/* Stats Overview */}
                    <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Overview</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <Users className="w-5 h-5 text-primary-500 mr-2" />
                                    <span className="text-slate-600">Total Employees</span>
                                </div>
                                <span className="font-semibold text-foreground">{stats?.totalEmployees || 0}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <GraduationCap className="w-5 h-5 text-primary-500 mr-2" />
                                    <span className="text-slate-600">Active Courses (In Progress)</span>
                                </div>
                                <span className="font-semibold text-foreground">{stats?.activeCourses || 0}</span>
                            </div>
                            <div 
                                className="flex items-center justify-between cursor-pointer hover:bg-slate-50 rounded-lg transition-colors py-1 px-1 -mx-1"
                                onClick={handleDueTrainingClick}
                            >
                                <div className="flex items-center">
                                    <AlertCircle className="w-5 h-5 text-amber-500 mr-2" />
                                    <span className="text-slate-600">Training Due (Next 7 Days)</span>
                                </div>
                                <span className="font-semibold text-amber-600">{stats?.dueTrainings || 0}</span>
                            </div>
                        </div>
                    </div>
                    {/* Quick Actions */}
                    <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
                        <div className="space-y-2">
                            <button 
                                onClick={() => navigate('/admin/employees')}
                                className="flex items-center w-full px-4 py-2 text-left text-primary-600 hover:bg-primary-50 rounded-lg transition duration-150"
                            >
                                <Users size={16} className="mr-2"/> Manage Users
                            </button>
                            <button 
                                onClick={() => navigate('/admin/courses')}
                                className="flex items-center w-full px-4 py-2 text-left text-primary-600 hover:bg-primary-50 rounded-lg transition duration-150"
                            >
                                <BookOpen size={16} className="mr-2"/> Course Management
                            </button>
                            <button 
                                onClick={() => navigate('/admin/certificates')}
                                className="flex items-center w-full px-4 py-2 text-left text-primary-600 hover:bg-primary-50 rounded-lg transition duration-150"
                            >
                                <CheckCircle size={16} className="mr-2"/> View Certificates
                            </button>
                        </div>
                    </div>
                    {/* Pending Invites */}
                    <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                        <h3 className="text-lg font-semibold text-foreground mb-4">Pending Invitations</h3>
                        {pendingInvites && pendingInvites.length > 0 ? (
                            <div className="space-y-4">
                                {pendingInvites.map((invite: any) => (
                                    <div key={invite.id} className="p-4 bg-slate-50 rounded-lg">
                                        <p className="font-medium text-foreground">{invite.name}</p>
                                        <p className="text-sm text-slate-500">
                                            {invite.invited_email}
                                            <br />
                                            Invited on {new Date(invite.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-center">
                                <UserPlus className="w-12 h-12 text-slate-400 mb-4" />
                                <p className="text-slate-600 mb-2">No pending invitations</p>
                                <p className="text-sm text-slate-500">Invite new employees to join your organization</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Recent Activity */}
            <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200 mb-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
                {recentActivity && recentActivity.length > 0 ? (
                    <div className="space-y-4">
                        {recentActivity.map((activity: any, index: number) => (
                            <div key={index} className="flex items-start space-x-3">
                                <div className="flex-shrink-0">
                                    <Activity className="w-5 h-5 text-primary-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-foreground">{activity.description}</p>
                                    <p className="text-xs text-slate-500">{formatTimestamp(activity.timestamp)} CST</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <Activity className="w-12 h-12 text-slate-400 mb-4" />
                        <p className="text-slate-600 mb-2">No recent activity</p>
                        <p className="text-sm text-slate-500">Activity will appear here as employees complete courses and earn certificates</p>
                    </div>
                )}
            </div>

            {/* Employee Training Status */}
            {/* <div className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-shadow duration-200 border border-slate-200">
                <h3 className="text-lg font-semibold text-foreground mb-4">Training Status</h3>
                {employeeTraining && employeeTraining.length > 0 ? (
                    <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
                        {employeeTraining
                            .filter(employee => employee.pendingCourses?.length > 0)
                            .sort((a, b) => b.progress - a.progress)
                            .map(employee => (
                                <div key={employee.id} className="border-l-4 border-sky-400 pl-3">
                                    <div className="flex items-center mb-2">
                                        {employee.image ? (
                                            <img 
                                                src={employee.image} 
                                                alt={employee.name} 
                                                className="w-8 h-8 rounded-full mr-2"
                                            />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center mr-2">
                                                <span className="text-sm font-medium text-primary-600">
                                                    {employee.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <p className="font-medium text-foreground">{employee.name}</p>
                                            <p className="text-sm text-slate-500">{employee.role}</p>
                                        </div>
                                        <div className={`ml-2 text-sm font-medium ${
                                            employee.progress === 0 
                                                ? 'text-red-500' 
                                                : employee.progress === 100 
                                                    ? 'text-green-600' 
                                                    : 'text-amber-500'
                                        }`}>
                                            {employee.progress}%
                                        </div>
                                    </div>
                                    <div className={`w-full bg-slate-100 rounded-full h-2 border ${employee.progress === 0 ? 'border-red-500' : 'border-slate-300'}`}>
                                        <div
                                            className="bg-green-600 h-2 rounded-full"
                                            style={{ width: `${employee.progress}%` }}
                                        />
                                    </div>
                                    {employee.pendingCourses?.length > 0 && (
                                        <p className="text-sm text-slate-500 mt-1">
                                            Pending: {employee.pendingCourses.join(', ')}
                                        </p>
                                    )}
                                    <p className="text-sm text-slate-500">
                                        Due: {employee.dueDate ? new Date(employee.dueDate).toLocaleDateString() : 'No due date set'}
                                    </p>
                                </div>
                            ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-8 text-center">
                        <UserCheck className="w-12 h-12 text-slate-400 mb-4" />
                        <p className="text-slate-600 mb-2">No training data available</p>
                        <p className="text-sm text-slate-500">Assign courses to employees to track their training progress</p>
                    </div>
                )}
            </div> */}

            {/* Due Training Modal */}
            <DueTrainingModal
                isOpen={isDueTrainingModalOpen}
                onClose={closeDueTrainingModal}
                dueTrainingData={stats?.courses || []}
            />
        </div>
    );
} 